import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FolderIcon from "@mui/icons-material/Folder";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import React, {
  cloneElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createRoot } from "react-dom/client";
import { CellComponent, ColumnDefinition } from "tabulator-tables";
import { Legacy } from "../../api/compute/Legacy";
import Modal from "../../components/Modal";
import Tabulator, { EventsType } from "../../components/Tabulator";
import { useFeedbackSystem } from "../../hooks/useFeedbackSystem";
import { useAuth } from "../../hooks/useAuth";

type SyncProgress = {
  startedAt?: string | number | null;
  elapsedMs?: number;
  phase?: "LISTING" | "INGEST" | "PURGE" | "DICTIONARY" | string;
  total?: number;
  processed?: number;
  currentKey?: string | null;
  ingested?: string[];
  purged?: string[];
  failed?: string[];
};

type SyncReport = {
  startedAt?: string | number | null;
  durationMs?: number;
  ingested?: string[];
  purged?: string[];
  failed?: string[];
  error?: string | null;
};

type RagStatus = {
  running: boolean;
  progress?: SyncProgress;
  lastReport?: SyncReport;
};

// Instant can arrive as an ISO string or an epoch number (seconds or millis).
function formatInstant(value?: string | number | null): string {
  if (value == null) return "—";
  const ms =
    typeof value === "number" ? (value < 1e12 ? value * 1000 : value) : value;
  const date = new Date(ms);
  return isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function formatDuration(ms?: number): string {
  if (ms == null || isNaN(ms)) return "—";
  if (ms < 1000) return `${ms} ms`;
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(" ");
}

// Ingested / purged / failed counts, with the full file list on hover.
function CountChips({
  ingested,
  purged,
  failed,
}: Pick<SyncReport, "ingested" | "purged" | "failed">) {
  const chip = (
    label: string,
    items: string[] | undefined,
    color: "success" | "default" | "error"
  ) => (
    <Tooltip title={items && items.length > 0 ? items.join(", ") : "None"} arrow>
      <Chip
        size="small"
        variant="outlined"
        color={color}
        label={`${label}: ${items?.length ?? 0}`}
      />
    </Tooltip>
  );
  return (
    <Box display="flex" gap={1} flexWrap="wrap">
      {chip("Ingested", ingested, "success")}
      {chip("Purged", purged, "default")}
      {chip("Failed", failed, "error")}
    </Box>
  );
}

export default function ChatDocs() {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const feedback = useFeedbackSystem();

  const getFileNameFromRawName = useCallback((rawName: string) => {
    const fileName = rawName.split("/").pop();
    return fileName;
  }, []);

  const auth = useAuth();
  useEffect(() => {
    console.log("auth", auth);
    console.log("cookies", document.cookie);
  }, [auth]);

  const [folders, setFolders] = useState<string[]>([]);
  const tableRef = React.useRef<any>(null);
  const legacyAPI = useMemo(
    () => new Legacy(auth.user.additionalCookies[0].value),
    [auth.user.additionalCookies]
  );

  //#region - RAG ingestion state & handlers
  const [ragStatus, setRagStatus] = useState<RagStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isWiping, setIsWiping] = useState(false);
  const [showWipeConfirm, setShowWipeConfirm] = useState(false);

  const loadRagStatus = useCallback(() => {
    setIsLoadingStatus(true);
    return legacyAPI
      .getRagStatus()
      .then(
        (response) => {
          setRagStatus(response);
        },
        (error) => {
          feedback.show({
            type: "error",
            message: `Error loading RAG status: ${error?.message ?? error}`,
          });
        }
      )
      .finally(() => {
        setIsLoadingStatus(false);
      });
  }, [feedback, legacyAPI]);

  const handleReloadState = useCallback(() => {
    loadRagStatus();
  }, [loadRagStatus]);

  const handleSync = useCallback(() => {
    setIsSyncing(true);
    legacyAPI
      .syncRag()
      .then(
        () => {
          feedback.show({
            type: "success",
            message: "RAG sync started! 😊",
          });
          loadRagStatus();
        },
        (error) => {
          if (error?.response?.status === 409) {
            feedback.show({
              type: "warning",
              message: "A sync is already running.",
            });
          } else {
            feedback.show({
              type: "error",
              message: `Error starting sync: ${error?.message ?? error}`,
            });
          }
        }
      )
      .finally(() => {
        setIsSyncing(false);
      });
  }, [feedback, legacyAPI, loadRagStatus]);

  const handleWipe = useCallback(() => {
    setIsWiping(true);
    legacyAPI
      .wipeRag()
      .then(
        () => {
          feedback.show({
            type: "success",
            message: "RAG corpus wiped successfully.",
          });
          loadRagStatus();
        },
        (error) => {
          if (error?.response?.status === 409) {
            feedback.show({
              type: "warning",
              message: "A sync is running: cannot wipe now.",
            });
          } else {
            feedback.show({
              type: "error",
              message: `Error wiping corpus: ${error?.message ?? error}`,
            });
          }
        }
      )
      .finally(() => {
        setIsWiping(false);
        setShowWipeConfirm(false);
      });
  }, [feedback, legacyAPI, loadRagStatus]);
  //#endregion

  const filePropertiesMap = useMemo(
    () => ({
      fileName: {
        label: "File Name",
        formatter: (cell: CellComponent) => {
          const value = cell.getValue();
          const fileName = value.split("/").pop();
          return fileName;
        },
      },
      lastModified: {
        label: "Last Modified",
        formatter: (cell: CellComponent) => {
          const value = cell.getValue();
          const date = new Date(value);
          return date.toISOString().split("T")[0];
        },
      },
      size: {
        label: "Size",
        formatter: (cell: CellComponent) => {
          const value = cell.getValue();
          const sizeInBytes = parseInt(value);
          const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);
          return `${sizeInMB} MB`;
        },
      },
    }),
    []
  );

  const files = useRef<any[]>([]);

  const generateColuns = useCallback(
    (files) => {
      const columns: ColumnDefinition[] = [
        // {
        //   formatter: "rowSelection",
        //   titleFormatter: "rowSelection",
        //   title: "",
        //   resizable: false,
        //   frozen: true,
        //   widthGrow: 0.3,
        //   hozAlign: "center",
        //   headerHozAlign: "center",
        //   headerSort: false,
        // },
      ];
      if (files.length > 0) {
        const file = files[0];
        Object.keys(file).forEach((key) => {
          columns.push({
            title: filePropertiesMap[key].label,
            field: key,
            // widthGrow: 1,
            hozAlign: "left",
            formatter: filePropertiesMap[key].formatter,
          });
        });
      }
      columns.push({
        title: "",
        frozen: true,

        field: "actions",
        widthGrow: 0.5,
        hozAlign: "center",
        headerHozAlign: "center",
        headerSort: false,
        cellClick: (e, cell) => {
          cell.getRow().deselect();
        },
        formatter: (
          cell: CellComponent,
          formatterParams: any,
          onRendered: (callback: () => void) => void
        ) => {
          // cell - the cell component
          // formatterParams - parameters set for the column
          // onRendered - function to call when the formatter has been rendered
          const renderFn = () => {
            const cellEl = cell.getElement();
            if (cellEl) {
              const formatterCell = cellEl.querySelector(".formatterCell");
              if (formatterCell) {
                const CompWithMoreProps = cloneElement(
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      bgcolor: "black",
                      color: "white",
                      p: 0,
                      textTransform: "none",
                      ":hover": { boxShadow: 2, bgcolor: "black" },
                    }}
                    onClick={() => {
                      const fileName = cell.getData().fileName;
                      legacyAPI.deleteFile(fileName).then(
                        (response) => {
                          feedback.show({
                            type: "success",
                            message: `${getFileNameFromRawName(
                              fileName
                            )} deleted successfully! 😊`,
                          });
                          // alert(
                          //   `${getFileNameFromRawName(
                          //     fileName
                          //   )} deleted successfully 😊`
                          // );
                          const row = cell.getRow();
                          row.delete();
                        },
                        (error) => {
                          // alert(error.message);
                          feedback.show({
                            type: "error",
                            message: `${error.message}`,
                          });
                        }
                      );
                    }}
                  >
                    <Typography fontSize={"0.7vw"}>Delete</Typography>
                  </Button>,
                  { cell }
                );
                const root = createRoot(formatterCell);
                root.render(CompWithMoreProps);
              }
            }
          };

          if (onRendered) {
            onRendered(renderFn); // initial render only.
          }

          setTimeout(() => {
            renderFn(); // render every time cell value changed.
          }, 0);
          return '<div class="formatterCell"></div>';
        },
      });
      return columns;
    },
    [feedback, filePropertiesMap, getFileNameFromRawName, legacyAPI]
  );
  const tableOptions = useMemo(
    () => ({
      layout: "fitColumns",
      selectableRows: false,
      width:"100%",
      height:"100%"
    }),
    []
  );

  const tableEvents: EventsType = useMemo(
    () => [
      {
        event: "tableBuilt",
        callback: () => {
          //   setIsTableReady(true);
        },
      },
      {
        event: "tableDestroyed",
        callback: () => {
          //   setIsTableReady(false);
        },
      },
      {
        event: "rowSelectionChanged",
        callback: (data, rows, selected, deselected) => {
          // if (!selectionAllowed.current) {
          //   selected.forEach((row) => {
          // 	row.deselect();
          //   });
          // }
          // if (
          //   selectedAccount.current &&
          //   selectionAllowed.current &&
          //   !(selected.length === 0 && deselected.length === 0)
          // ) {
          //   const portfolioIds = data.map((item) => item.id);
          //   changeJsonHandler(selectedAccount.current, portfolioIds);
          // }
        },
      },
    ],
    []
  );

  const onSelectFolderHandler = useCallback(
    async (folder: string) => {
      setIsLoadingFiles(true);
      setSelectedFolder(folder);
      // feedback.show({
      //   type: "success",
      //   message: `Loading files from ${folder} folder...`,
      // });
      legacyAPI
        .getFilesFromFolder(folder)
        .then(
          (response) => {
            files.current = response;
          },
          (error) => {
            files.current = [];
            // alert(error.message);
            feedback.show({
              type: "error",
              message: `${error.message}`,
            });
          }
        )
        .finally(() => {
          setIsLoadingFiles(false);
          const tableInstance = tableRef?.current?.getTableInstance();
          tableInstance.setColumns(generateColuns(files.current));
          tableInstance.replaceData(files.current);
        });
    },
    [feedback, generateColuns, legacyAPI]
  );

  const onClickUploadHandler = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.csv,.xls,.xlsx";
    input.multiple = true;
    input.onchange = (event) => {
      const files = (event.target as HTMLInputElement).files;
      if (files && selectedFolder) {
        const fileArray = Array.from(files);
        legacyAPI
          .uploadFile(fileArray[0], selectedFolder)
          .then(
            (response) => {
              // alert(
              //   `${getFileNameFromRawName(
              //     fileArray[0].name
              //   )} uploaded successfully`
              // );
              feedback.show({
                type: "success",
                message: `${getFileNameFromRawName(
                  fileArray[0].name
                )} uploaded successfully! 😊`,
              });
              onSelectFolderHandler(selectedFolder);
            },
            (error) => {
              feedback.show({
                type: "error",
                message: `${error.message}`,
              });
              // alert(error.message);
            }
          )
          .finally(() => {
            input.remove();
          });
        // Handle the selected files here
      }
    };
    input.click();
  }, [
    feedback,
    getFileNameFromRawName,
    legacyAPI,
    onSelectFolderHandler,
    selectedFolder,
  ]);

  //#region - on landing
  useEffect(() => {
    setIsLoading(true);
    // if (auth?.user?.additionalCookies?.value) {
    legacyAPI
      .getFolders()
      .then(
        (response) => {
          setFolders(response);
        },
        (error) => {
          // alert(error.message);
          feedback.show({
            type: "error",
            message: `${error.message}`,
          });
        }
      )
      .then(() => {})
      .finally(() => {
        setIsLoading(false);
      });
    // }
  }, [feedback, legacyAPI]);
  //#endregion

  //#region - on landing: populate the RAG status panel
  useEffect(() => {
    loadRagStatus();
  }, [loadRagStatus]);
  //#endregion

  //#region - cleanup
  useEffect(() => {
    return () => {
      setSelectedFolder(null);
      files.current = [];
      setFolders([]);
      setRagStatus(null);
    };
  }, []);
  //#endregion

  return isLoading ? (
    <Box
      display={"flex"}
      gap={2}
      flexDirection={"column"}
      alignItems={"center"}
      justifyContent={"center"}
      width={"100%"}
      height={"100%"}
    >
      <Typography>Loading data...</Typography>
      <CircularProgress color="secondary" />
    </Box>
  ) : (
    <Box
      height={"100%"}
      width={"100%"}
      gap={1}
      display={"flex"}
      flexDirection={"column"}
    >
      {/* RAG ingestion toolbar + status */}
      <Box
        display={"flex"}
        alignItems={"center"}
        gap={2}
        flexWrap={"wrap"}
        boxShadow={3}
        borderRadius={2}
        p={1}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
          RAG state:
        </Typography>
        {isLoadingStatus ? (
          <CircularProgress size={18} color="secondary" />
        ) : ragStatus ? (
          <Chip
            size="small"
            label={ragStatus.running ? "Running" : "Idle"}
            color={ragStatus.running ? "warning" : "success"}
          />
        ) : (
          <Chip size="small" label="Unknown" />
        )}

        <Box flexGrow={1} />

        <Button
          onClick={handleReloadState}
          startIcon={
            isLoadingStatus ? <CircularProgress size={16} /> : <RefreshIcon />
          }
          variant="contained"
          color="secondary"
          size="small"
          disabled={isLoadingStatus}
        >
          Reload state
        </Button>
        <Button
          onClick={handleSync}
          startIcon={
            isSyncing ? <CircularProgress size={16} /> : <SyncIcon />
          }
          variant="contained"
          color="secondary"
          size="small"
          disabled={isSyncing || ragStatus?.running === true}
        >
          Sync
        </Button>
        <Button
          onClick={() => setShowWipeConfirm(true)}
          startIcon={
            isWiping ? <CircularProgress size={16} /> : <DeleteForeverIcon />
          }
          variant="contained"
          size="small"
          disabled={isWiping || ragStatus?.running === true}
          sx={{
            bgcolor: "red",
            color: "white",
            ":hover": { bgcolor: "#c62828" },
          }}
        >
          Wipe
        </Button>
      </Box>

      {/* RAG status detail */}
      <Box
        boxShadow={3}
        borderRadius={2}
        p={1.5}
        display={"flex"}
        flexDirection={"column"}
        gap={1.5}
      >
        {isLoadingStatus && !ragStatus ? (
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <CircularProgress size={16} color="secondary" />
            <Typography variant="body2" color="textSecondary">
              Loading status…
            </Typography>
          </Box>
        ) : !ragStatus ? (
          <Typography variant="body2" color="textSecondary">
            Status unavailable.
          </Typography>
        ) : (
          <>
            {/* Progress — only while a sync is running */}
            {ragStatus.running && ragStatus.progress && (
              <Box display={"flex"} flexDirection={"column"} gap={1}>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  gap={1}
                  flexWrap={"wrap"}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    In progress
                  </Typography>
                  <Chip
                    size="small"
                    color="info"
                    label={ragStatus.progress.phase ?? "—"}
                  />
                  <Typography variant="caption" color="textSecondary">
                    started {formatInstant(ragStatus.progress.startedAt)} ·
                    elapsed {formatDuration(ragStatus.progress.elapsedMs)}
                  </Typography>
                </Box>
                {(ragStatus.progress.total ?? 0) > 0 && (
                  <Box display={"flex"} alignItems={"center"} gap={1}>
                    <Box flexGrow={1}>
                      <LinearProgress
                        variant="determinate"
                        color="secondary"
                        value={Math.min(
                          100,
                          Math.round(
                            ((ragStatus.progress.processed ?? 0) /
                              ragStatus.progress.total!) *
                              100
                          )
                        )}
                      />
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {ragStatus.progress.processed ?? 0} /{" "}
                      {ragStatus.progress.total}
                    </Typography>
                  </Box>
                )}
                {ragStatus.progress.currentKey && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    noWrap
                    title={ragStatus.progress.currentKey}
                  >
                    Current: {ragStatus.progress.currentKey}
                  </Typography>
                )}
                <CountChips
                  ingested={ragStatus.progress.ingested}
                  purged={ragStatus.progress.purged}
                  failed={ragStatus.progress.failed}
                />
                <Divider />
              </Box>
            )}

            {/* Last completed sync */}
            <Box display={"flex"} flexDirection={"column"} gap={1}>
              <Box
                display={"flex"}
                alignItems={"center"}
                gap={1}
                flexWrap={"wrap"}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Last sync
                </Typography>
                {ragStatus.lastReport?.startedAt == null ? (
                  <Chip size="small" variant="outlined" label="Never run" />
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    {formatInstant(ragStatus.lastReport.startedAt)} · took{" "}
                    {formatDuration(ragStatus.lastReport.durationMs)}
                  </Typography>
                )}
              </Box>
              {ragStatus.lastReport?.startedAt != null && (
                <CountChips
                  ingested={ragStatus.lastReport.ingested}
                  purged={ragStatus.lastReport.purged}
                  failed={ragStatus.lastReport.failed}
                />
              )}
              {ragStatus.lastReport?.error && (
                <Alert severity="error" sx={{ py: 0 }}>
                  {ragStatus.lastReport.error}
                </Alert>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Folders + file manager */}
      <Box
        flex={1}
        minHeight={0}
        width={"100%"}
        gap={2}
        borderRadius={1}
        display={"flex"}
      >
      <Box
        // width={"100%"}
        display={"flex"}
        gap={1}
        boxShadow={3}
        justifyContent={"flex-start"}
        alignContent={"flex-start"}
        p={1}
        // overflow={"auto"}
        borderRadius={2}
        flexDirection={"column"}
      >
        <List
          subheader={<Typography>Folders</Typography>}
          dense={true}
          sx={{
            gap: 1,
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          {folders.map((folder, index) => (
            <ListItem
              sx={{
                cursor: "pointer",
                borderRadius: 1,
                boxShadow: folder === selectedFolder ? 3 : 0,
              }}
              key={`${index}-${folder}`}
              onClick={() => onSelectFolderHandler(folder)}
            >
              <ListItemIcon>
                <FolderIcon
                  color={folder === selectedFolder ? "secondary" : "primary"}
                />
              </ListItemIcon>
              <ListItemText
                primary={folder}
                // secondary={'Secondary text'}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {selectedFolder != null && (
        <>
          <Box
            width={"100%"}
            height={"100%"}
            display={"block"}
            sx={{ overflowY: "hidden", overflowX: "hidden" }}
            gap={1}
            boxShadow={3}
            justifyContent={"flex-start"}
            alignContent={"flex-start"}
            borderRadius={2}
            flexDirection={"column"}
            position={"relative"}
          >
            {isLoadingFiles && (
              <Box
                position={"absolute"}
                top={0}
                zIndex={1}
                sx={{
                  backdropFilter: "blur(2px)",
                  backgroundColor: "rgba(0, 0, 0, 0.09)",
                }}
                left={0}
                display={"flex"}
                gap={2}
                flexDirection={"column"}
                alignItems={"center"}
                justifyContent={"center"}
                width={"100%"}
                height={"100%"}
              >
                <Typography>Loading files...</Typography>
                <CircularProgress color="secondary" />
              </Box>
            )}

            <Tabulator
              ref={tableRef}
              options={tableOptions}
              events={tableEvents}
            />
          </Box>

          <Box
            boxShadow={3}
            display={"flex"}
            gap={1}
            width={"100%"}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.style.backgroundColor = "#f1f1f1";
              e.currentTarget.style.border = "2px dashed #000";
            }}
            onDragLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#f9f9f9";
              e.currentTarget.style.border = "2px dashed #ccc";
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.style.backgroundColor = "#f9f9f9";
              e.currentTarget.style.border = "2px dashed #ccc";
              const files = Array.from(e.dataTransfer.files);
              const acceptedFiles = files.filter(
                (file) =>
                  file.type === "application/pdf" ||
                  file.type === "application/vnd.ms-excel" ||
                  file.type === "text/csv" ||
                  file.type ===
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              );
              if (acceptedFiles.length > 0) {
                legacyAPI.uploadFile(acceptedFiles[0], selectedFolder).then(
                  (response) => {
                    // alert(
                    //   `${getFileNameFromRawName(
                    //     acceptedFiles[0].name
                    //   )} uploaded successfully`
                    // );
                    feedback.show({
                      type: "success",
                      message: `${getFileNameFromRawName(
                        acceptedFiles[0].name
                      )} uploaded successfully! 😊`,
                    });
                    onSelectFolderHandler(selectedFolder);
                  },
                  (error) => {
                    // alert(error.message);
                    feedback.show({
                      type: "error",
                      message: `${error.message}`,
                    });
                  }
                );
              } else {
                feedback.show({
                  type: "warning",
                  message: "Only PDF, Excel, or CSV files are accepted!",
                });
              }
            }}
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              padding: 2,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Button
              onClick={onClickUploadHandler}
              startIcon={<CloudUploadIcon />}
              variant="contained"
              color="secondary"
              size="small"
            >
              <Typography>
                Select a file to upload in <strong>{selectedFolder}</strong>{" "}
                folder
              </Typography>
            </Button>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Or drag and drop PDF, Excel, or CSV files here
            </Typography>
          </Box>
        </>
      )}
      </Box>

      {showWipeConfirm && (
        <Modal
          buttonsEnalbed
          onClose={() => setShowWipeConfirm(false)}
          headerConfig={{
            headerContent: (
              <Typography>
                <strong>Wipe RAG corpus</strong>
              </Typography>
            ),
          }}
          buttons={[
            {
              name: "Wipe",
              style: { backgroundColor: "red", color: "white" },
              disabled: isWiping,
              callback: handleWipe,
            },
            {
              name: "Cancel",
              callback: () => setShowWipeConfirm(false),
            },
          ]}
        >
          <Typography>
            Questa operazione cancella l'intero corpus del RAG (indici, markdown
            e documenti originali) ed è irreversibile. Sei sicuro di voler
            procedere?
          </Typography>
        </Modal>
      )}
    </Box>
  );
}
